import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react'
import { Snackbar, SnackbarOrigin, Alert, AlertColor, Box } from '@mui/material'
import useBreadcrumb from '@context/BreadCrumbContext/useBreadcrumb'
import localStore from '@service/local-store'
import FlagIcon from '@mui/icons-material/Flag'

interface ToastMessage {
  id: string
  message: ReactNode
  type: AlertColor | 'flag'
  isModal: boolean
  duration?: number
  persistent?: boolean
}

interface ToastParams {
  id?: string // Optional, defaults to a generated value if not provided
  message: ReactNode
  type: AlertColor | 'flag' // Matches Material-UI Alert's severity types
  isModal?: boolean
  duration?: number // Custom duration in milliseconds
  persistent?: boolean // If true, toast stays until manually closed
  dialogProps?: {
    [key: string]: unknown
  }
}

interface ToastContextProps {
  showToast: (params: ToastParams) => void
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined)

const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [queue, setQueue] = useState<ToastMessage[]>([])
  const { breadcrumbs } = useBreadcrumb()
  const [currentToast, setCurrentToast] = useState<ToastMessage | null>(null)
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<SnackbarOrigin>({
    vertical: 'top',
    horizontal: 'left',
  })

  const isMobile = Boolean(localStore.get('isMobile'))
  const isTablet = Boolean(localStore.get('isTablet'))
  const isFullScreen = !(isTablet || isMobile)
  const [topValue, setTopValue] = useState(0)
  const [breacrumbBottomValue, setBreacrumbBottomValue] = useState(0)
  const [breacrumbLeftValue, setBreacrumbLeftValue] = useState(0)
  const [headerLength, setHeaderLength] = useState(0)

  //MuiBreadcrumbs-root
  useEffect(() => {
    const actionsDialogElement = document.querySelector('.MuiDialogTitle-root')
    const actionsDialogH2Element = document
      .querySelector('.MuiDialogTitle-root')
      ?.querySelector('h2')
    const actionsHeaderElement = document.querySelector('.MuiBreadcrumbs-root')
      ? document.querySelector('.MuiBreadcrumbs-root')
      : document.querySelector('.MuiToolbar-root')

    if (currentToast?.isModal) {
      if (actionsDialogElement && actionsDialogH2Element) {
        const rect = actionsDialogElement.getBoundingClientRect()
        const rectH2Value = actionsDialogH2Element.getBoundingClientRect()
        setTopValue(rectH2Value.bottom + 5)
        setHeaderLength(rect.right - rect.left - 20)
      }
    } else if (actionsHeaderElement) {
      const rect = actionsHeaderElement.getBoundingClientRect()
      setTopValue(rect.bottom + 15)
      setHeaderLength(rect.right - rect.left)
    } else {
      // Fallback for admin pages or when elements aren't found
      const optumLogo = document.getElementById('brandIcon')
      if (optumLogo) {
        const logoRect = optumLogo.getBoundingClientRect()
        setTopValue(logoRect.bottom + 15)
      } else {
        setTopValue(80) // Default fallback
      }
    }
  }, [topValue, currentToast])

  useEffect(() => {
    const actionsElement = document.querySelector('.MuiBreadcrumbs-root')
    const actionToolbarElement = document.querySelector('.MuiToolbar-root')
    const reviewSurveyHeader = document.querySelector('header')
    const optumLogo = document.getElementById('brandIcon')
    const optumLogoRect = optumLogo?.getBoundingClientRect()

    if (actionsElement) {
      const rect = actionsElement.getBoundingClientRect()
      setBreacrumbBottomValue(rect.bottom + 30)
      optumLogoRect
        ? setBreacrumbLeftValue(optumLogoRect.left)
        : setBreacrumbLeftValue(rect.left)
    } else if (actionToolbarElement) {
      const rect = actionToolbarElement.getBoundingClientRect()
      setBreacrumbBottomValue(rect.bottom + 20)
      optumLogoRect
        ? setBreacrumbLeftValue(optumLogoRect.left)
        : setBreacrumbLeftValue(rect.left + 20)
    } else if (reviewSurveyHeader) {
      const rect = reviewSurveyHeader.getBoundingClientRect()
      setBreacrumbBottomValue(rect.bottom)
      setBreacrumbLeftValue(!isFullScreen ? rect.left : rect.left + 80)
    } else if (optumLogoRect) {
      // Fallback for admin pages - position below logo
      setBreacrumbBottomValue(optumLogoRect.bottom + 50)
      setBreacrumbLeftValue(optumLogoRect.left + 50)
    } else {
      // Final fallback
      setBreacrumbBottomValue(125)
      setBreacrumbLeftValue(81)
    }
    //eslint-disable-next-line
  }, [breacrumbBottomValue, breacrumbLeftValue, currentToast, isFullScreen])

  useEffect(() => {
    setPosition(
      isMobile || isTablet
        ? { vertical: 'top', horizontal: 'center' }
        : currentToast?.isModal
          ? { vertical: 'top', horizontal: 'center' }
          : { vertical: 'top', horizontal: 'left' }
    )

    //eslint-disable-next-line
  }, [isMobile, currentToast, isTablet])

  useEffect(() => {
    if (!currentToast && queue.length > 0) {
      const nextToast = queue[0]
      setQueue((prevQueue) => prevQueue.slice(1))
      setCurrentToast(nextToast)
      setOpen(true)
    }
    //eslint-disable-next-line
  }, [queue, currentToast, breadcrumbs])

  const showToast = ({
    id = `${Date.now()}`,
    message,
    type,
    isModal = false,
    duration,
    persistent = false,
  }: ToastParams) => {
    window.scroll({ top: 0, behavior: 'smooth' })
    const newToast: ToastMessage = {
      id,
      message,
      type,
      isModal,
      duration,
      persistent,
    }
    setQueue((prevQueue) => [...prevQueue, newToast])
  }

  const handleClose = () => {
    setOpen(false)
    setTimeout(() => {
      setCurrentToast(null)
    }, 300)
  }

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={
          currentToast?.persistent ? null : currentToast?.duration || 3000
        }
        onClose={handleClose}
        anchorOrigin={position}
        key={currentToast?.id}
        sx={{
          ...(currentToast?.isModal && isFullScreen
            ? {
                '&.MuiSnackbar-root': {
                  left: '50%',
                  top: topValue,
                  width: headerLength,
                  maxWidth: '95%',
                  transform: 'translateX(-50%)',
                },
              }
            : isMobile || isTablet
              ? {
                  '&.MuiSnackbar-root': {
                    left: '46%',
                    top: topValue,
                    maxWidth: '80%',
                    transform: 'translateX(-50%)',
                  },
                }
              : {
                  '&.MuiSnackbar-root': {
                    left: breacrumbLeftValue,
                    top: breacrumbBottomValue,
                    width: 'auto',
                    maxWidth: '90%',
                  },
                }),
          width: { xs: '100%' },
        }}
      >
        <Box>
          {currentToast ? (
            <Alert
              onClose={handleClose}
              severity={
                currentToast.type === 'flag' ? 'info' : currentToast.type
              }
              icon={currentToast.type === 'flag' ? <FlagIcon /> : undefined}
              sx={({ ues }) => ({
                width: '100%',
                background:
                  ues[
                    currentToast.type === 'flag' ? 'info' : currentToast.type
                  ]?.[5],
                borderColor:
                  ues[
                    currentToast.type === 'flag' ? 'info' : currentToast.type
                  ]?.[70],
              })}
            >
              {currentToast.message}
            </Alert>
          ) : (
            <></>
          )}
        </Box>
      </Snackbar>
    </ToastContext.Provider>
  )
}

export default ToastProvider

export const useToast = (): ToastContextProps => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
